import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import PageShell from '../components/PageShell';
import Card from '../components/Card';
import { listNews, createNews, updateNews, deleteNews } from '../api/news';
import { dumpAllTenants } from '../api/tenants';
import { getLastActivity } from '../api/audit';
import ONBOARDING from '../data/onboarding';
import './admin/AdminHubPage.css';
import './NewsAdminPage.css';

/* Administration › Gestion — les gestes d'exploitation de la plateforme, et le
   « Quoi de neuf » que les pages d'accueil des modules affichent.
   En tête, la SAUVEGARDE DE TOUS LES CLIENTS : elle vivait au bas de la liste
   des clients (2026-09-02), qui est un référentiel, pas un poste de manœuvre.
   Puis les nouveautés, PAR MODULE : une card par module, ses entrées datées,
   une ligne d'ajout. Des changements, pas des commentaires — libellé court,
   page concernée entre parenthèses. */

/* Sauvegarde de tous les clients : confirmation en DEUX temps. Ce bouton fait
   sortir de l'infrastructure les salaires nominatifs de tous les clients d'un
   coup ; un clic distrait ne doit pas y suffire. */
function DumpAllCard({ addToast }) {
  const { t } = useTranslation();
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);

  async function run() {
    if (!armed) { setArmed(true); return; }
    setBusy(true);
    try { await dumpAllTenants(); setArmed(false); }
    catch (e) { addToast && addToast(e.message, 'error'); }
    finally { setBusy(false); }
  }

  return (
    <Card title={t('admin:tenants.dumpAll.title')}>
      <div className="admin-dumpall">
        <div className="admin-dumpall__txt">
          <div className="admin-dumpall__lbl">{t('admin:tenants.dumpAll.label')}</div>
          <div className="admin-dumpall__desc">{t('admin:tenants.dumpAll.desc')}</div>
        </div>
        <button type="button" className={`admin-btn${armed ? '' : ' admin-btn--ghost'}`}
          onClick={run} disabled={busy}>
          {busy ? t('admin:tenants.dumpAll.busy')
                : armed ? t('admin:tenants.dumpAll.confirm') : t('admin:tenants.dumpAll.action')}
        </button>
      </div>
    </Card>
  );
}

const MODULES = [
  { key: 'people',    label: 'People' },
  { key: 'finance',   label: 'Finance' },
  { key: 'sales',     label: 'Ventes & Opérations' },
  { key: 'planning',  label: 'Planification' },
  { key: 'analytics', label: 'Analytique Services' },
  { key: 'cockpit',   label: 'Cockpit' },
  { key: 'general',   label: "Toute l'app" },
];

function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

/* Une nouveauté se compose d'un TITRE, puis, dépliés sous lui (2026-10-01) :
   la phrase qui dit ce qu'elle apporte, ce qui change (une ligne par
   changement) et le guide qui la montre. Toute modification renvoie la
   nouveauté ENTIÈRE : le serveur remplace, il ne fusionne pas. */
const full = (r, patch = {}) => ({
  module: r.module, news_date: r.news_date, text: r.text,
  intro: r.intro || null, changes: r.changes || [], guide_topic: r.guide_topic || null,
  ...patch,
});
const NO_FILL = { autoComplete: 'off', 'data-form-type': 'other', 'data-1p-ignore': 'true', 'data-lpignore': 'true' };

function GuideSelect({ value, onChange }) {
  const p = useP();
  return (
    <select className="lk-filter-select news-admin__guide" value={value || ''} onChange={e => onChange(e.target.value || null)}>
      <option value="">{p('Aucun guide')}</option>
      {ONBOARDING.map(gd => (
        <optgroup key={gd.id} label={gd.label}>
          {gd.topics.map(tp => <option key={tp.id} value={tp.id}>{tp.title}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

function NewsDetail({ row, onSave }) {
  const p = useP();
  return (
    <tr className="news-admin__detail">
      <td />
      <td colSpan={2}>
        <label>{p('Phrase')}
          <input type="text" {...NO_FILL} className="news-admin__text" maxLength={300} defaultValue={row.intro || ''}
            onBlur={e => { const v = e.target.value.trim(); if (v !== (row.intro || '')) onSave(full(row, { intro: v || null })); }} />
        </label>
        <label>{p('Ce qui change (une ligne par changement)')}
          <textarea {...NO_FILL} className="news-admin__text" rows={3} defaultValue={(row.changes || []).join('\n')}
            onBlur={e => {
              const v = e.target.value.split('\n').map(x => x.trim()).filter(Boolean);
              if (v.join('\n') !== (row.changes || []).join('\n')) onSave(full(row, { changes: v }));
            }} />
        </label>
        <label>{p('Guide lié')}
          <GuideSelect value={row.guide_topic} onChange={v => onSave(full(row, { guide_topic: v }))} />
        </label>
      </td>
    </tr>
  );
}

/* LE DERNIER USAGE PAR UTILISATEUR (demande d'Olivier, 2026-10-03) : sa
   dernière modification au journal, le plus récent d'abord. Une simple
   consultation n'y laisse pas de trace. */
function LastUsageCard() {
  const p = useP();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    getLastActivity().then(r => setRows((r && r.data) || [])).catch(() => setRows([]));
  }, []);
  const when = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
    return `${fmtDate(iso.slice(0, 10))} ${d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}`;
  };
  return (
    <Card title={p('Dernier usage par utilisateur')}>
      <table className="news-admin__table news-usage">
        <tbody>
          {(rows || []).map(u => (
            <tr key={u.id}>
              <td>{u.name}<span className="news-usage__mail">{u.email}</span></td>
              <td className={`news-usage__at${u.last_at ? '' : ' is-none'}`}>{when(u.last_at)}</td>
            </tr>
          ))}
          {rows && !rows.length && <tr><td className="news-usage__mail">{p('Aucun utilisateur')}</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

/* LES NOUVEAUTÉS DANS UNE SEULE CARTE (demande d'Olivier, 2026-10-03) : une
   ligne par nouveauté, son module choisi sur la ligne, plutôt qu'une carte par
   module. */
function NewsCard({ rows, onCreate, onUpdate, onDelete }) {
  const p = useP();
  const [date, setDate] = useState('');
  const [module, setModule] = useState('general');
  const [text, setText] = useState('');
  const [open, setOpen] = useState(null);

  const add = async () => {
    if (!date || !text.trim()) return;
    await onCreate({ module, news_date: date, text: text.trim() });
    setDate(''); setText('');
  };
  const modSelect = (value, onChange) => (
    <select className="news-admin__mod" value={value} onChange={e => onChange(e.target.value)}>
      {MODULES.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
    </select>
  );

  return (
    <Card title={p('Nouveautés')}>
      <table className="news-admin__table">
        <tbody>
          {rows.map(r => (
            <React.Fragment key={r.id}>
              <tr>
                <td className="news-admin__date">
                  <input type="date" {...NO_FILL} value={r.news_date || ''}
                    onChange={e => e.target.value && onUpdate(r.id, full(r, { news_date: e.target.value }))} />
                </td>
                <td className="news-admin__modc">{modSelect(r.module, v => onUpdate(r.id, full(r, { module: v })))}</td>
                <td>
                  <input type="text" {...NO_FILL} className="news-admin__text" defaultValue={r.text} maxLength={300}
                    onBlur={e => {
                      const v = e.target.value.trim();
                      if (v && v !== r.text) onUpdate(r.id, full(r, { text: v }));
                    }} />
                </td>
                <td className="news-admin__del">
                  <button type="button" className="news-admin__more" aria-expanded={open === r.id}
                    onClick={() => setOpen(open === r.id ? null : r.id)}>
                    {open === r.id ? p('Replier') : p('Détails')}
                  </button>
                  <button type="button" title={p('Supprimer')} onClick={() => onDelete(r.id)}>✕</button>
                </td>
              </tr>
              {open === r.id && <NewsDetail row={r} onSave={payload => onUpdate(r.id, payload)} />}
            </React.Fragment>
          ))}
          <tr className="news-admin__add">
            <td className="news-admin__date">
              <input type="date" {...NO_FILL} value={date} onChange={e => setDate(e.target.value)} />
            </td>
            <td className="news-admin__modc">{modSelect(module, setModule)}</td>
            <td>
              <input type="text" {...NO_FILL} className="news-admin__text" value={text} maxLength={300}
                placeholder={p('Nouvelle entrée…')}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') add(); }} />
            </td>
            <td className="news-admin__del">
              <button type="button" className="news-admin__addbtn" onClick={add} disabled={!date || !text.trim()}>
                {p('Ajouter')}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
}

export default function NewsAdminPage({ addToast }) {
  const p = useP();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = () => listNews()
    .then(r => setRows(r.data || []))
    .catch(e => addToast?.(e?.response?.data?.detail || e?.message || p('Échec du chargement'), 'error'))
    .finally(() => setLoading(false));
  useEffect(() => { refresh(); }, []); // eslint-disable-line

  const wrap = (fn) => async (...args) => {
    try { await fn(...args); refresh(); }
    catch (e) { addToast?.(e?.response?.data?.detail || e?.message || p("Échec de l'enregistrement"), 'error'); }
  };

  return (
    <PageShell title={p('Gestion')} subtitle={p('Sauvegarde de la plateforme, et le « Quoi de neuf » affiché sur les pages d’accueil des modules.')}>
      {/* DEUX COLONNES, PUIS LES NOUVEAUTÉS (demande d'Olivier, 2026-10-03) :
          le dernier usage par utilisateur et la sauvegarde côte à côte, la
          carte des nouveautés dessous. */}
      <div className="news-page">
        <div className="news-top">
          <LastUsageCard />
          <DumpAllCard addToast={addToast} />
        </div>
        {loading ? (
          <div className="hr-page__empty">{p('Chargement…')}</div>
        ) : (
          <NewsCard rows={rows}
            onCreate={wrap(createNews)}
            onUpdate={wrap(updateNews)}
            onDelete={wrap(deleteNews)} />
        )}
      </div>
    </PageShell>
  );
}

export { fmtDate };
